import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import useDebouncedValue from 'hooks/useDebouncedValue';

import type { SearchableFormValues } from 'components/SearchableFilter';
import { useAccountsFiltersQuery } from '../useAccountsFiltersQuery';

interface UseTeamFiltersProps {
  value: string[];
  onSubmit: (data: SearchableFormValues) => void;
  oemSegmentsIds?: string[];
  sentinelTeamsIds?: string[];
}

export default function useAccountsFilters({
  value,
  oemSegmentsIds = [],
  sentinelTeamsIds = [],
  onSubmit,
}: UseTeamFiltersProps) {
  const [searchValue, setSearchString, debouncedSearchValue] =
    useDebouncedValue('');

  const {
    data: { managedPlatformAccounts: { collection = [] } = {} } = {},
    loading,
    loadMore,
    loadingMore,
  } = useAccountsFiltersQuery({
    variables: {
      page: 1,
      text: debouncedSearchValue,
      oemSegmentsIds,
      sentinelTeamsIds,
    },
  });

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
    },
    [setSearchString],
  );

  const items = useMemo(
    () =>
      collection.map((record) => ({
        id: record.id,
        label: record.name,
        value: record.id,
      })),
    [collection],
  );

  const text = useMemo(() => {
    if (value.length === 0) return t`All Accounts`;

    if (value.length === 1)
      return items.find((item) => item.value === value[0])?.label || t`Account`;

    return t`Accounts (${value.length})`;
  }, [items, value]);

  return {
    items,
    text,
    loading,
    searchValue,
    debouncedSearchValue,
    onSearch,
    mode: 'async' as const,
    value,
    onSubmit,
    onLoadMore: loadMore,
    loadingMore,
  };
}
